import ImageMissing from "assets/placeholders/image-placeholder.jpg";
import { useCallback, useEffect, useRef, useState } from "react";
import { Transition } from "react-transition-group";
import { TransitionProps } from "react-transition-group/Transition";
import {
  MiniMap,
  ReactZoomPanPinchContentRef,
  TransformComponent,
  TransformWrapper,
  useControls,
} from "react-zoom-pan-pinch";
import Button from "shared/components/Button";
import { TimeoutRef } from "shared/helpers/helperModels";
import { roundNumber } from "shared/helpers/numberHelpers";

// IDEA: Keep functionality the same as PoiCarousel
// May want to combine them at some point to have the zoom functionality as
// well as the carousel

const INITIAL_SCALE = 1;
const MIN_SCALE = 0.15;
const MAX_SCALE = 5;
const ROUNDING_ACCURACY = 5;

interface PoiImageZoomProps {
  imageUrl: string | undefined;
}

const PoiImageZoom: React.FC<PoiImageZoomProps> = ({ imageUrl }) => {
  const imgSrc = imageUrl || ImageMissing;
  const [initialScale, setInitialScale] = useState<number>(INITIAL_SCALE);
  // Debounce behaviour for auto-hiding minimap
  const debounceTimeout = useRef<TimeoutRef>(null);
  const [actionComplete, setActionComplete] = useState<boolean>(false);

  const debounceInteraction = useCallback(() => {
    setActionComplete(false);
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    debounceTimeout.current = setTimeout(() => {
      setActionComplete(true);
    }, 900);
  }, []);

  useEffect(() => {
    // Cleanup timeout on unmount
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, []);

  // We can't automatically centre the image when the component loads.
  // To counter this, we scale the image once the image has loaded
  const wrapperRef = useRef<ReactZoomPanPinchContentRef>(null);
  // const imgRef = useRef<HTMLImageElement>(null);
  const scaleImageOnLoad: React.ReactEventHandler<HTMLImageElement> =
    useCallback((e) => {
      const wrapper = wrapperRef.current?.instance.wrapperComponent;
      const imgElement = e.currentTarget;
      // const imgElement = imgRef.current;
      let newScale = MIN_SCALE;
      if (wrapper && imgElement) {
        // Get element and wrapper sizes to determine the best fit scale
        const { height, width } = wrapper.getBoundingClientRect();
        const { naturalHeight, naturalWidth } = imgElement;
        const heightRatio = height / naturalHeight;
        const widthRatio = width / naturalWidth;
        // Take minimum ratio to ensure the image fits in both dimensions
        newScale = roundNumber(
          Math.min(heightRatio, widthRatio),
          ROUNDING_ACCURACY
        );
      }
      setInitialScale(newScale);
      // Centre image with new scale
      // NOTE: Done with timeout to force to end of call stack
      setTimeout(() => {
        if (wrapperRef.current) {
          wrapperRef.current.centerView(newScale);
        }
      }, 10);
    }, []);

  const minScale = Math.min(MIN_SCALE, initialScale);

  return (
    <TransformWrapper
      initialScale={initialScale}
      minScale={minScale}
      maxScale={MAX_SCALE}
      // wheel={{
      //   smoothStep: 0.001,
      //   step: 0.2,
      // }}
      // pinch={{
      //   step: 5,
      // }}
      // doubleClick={{ step: 0.7 }}
      // centerZoomedOut
      centerOnInit
      limitToBounds
      onTransformed={debounceInteraction}
      // NOTE: `onInit` no longer used as `scaleImageOnLoad` triggers
      // the event picked up by `onTransformed` when it completes
      // onInit={debounceInteraction}
      ref={wrapperRef}
    >
      {/* Zoomable image */}
      <TransformComponent
        wrapperClass="PoiImageZoomWrapper"
        children={
          <img
            onLoad={scaleImageOnLoad}
            src={imgSrc}
            alt={"Zoomable Scout Point"}
          />
        }
      />
      {/* UI Controls */}
      <PoiControls
        initialScale={initialScale}
        minScale={minScale}
        dimControls={actionComplete}
      />
      {/* Minimap */}
      <PoiMiniMap
        poiImage={<img src={imgSrc} alt={"Minimap of Scout Point"} />}
        hideMiniMap={actionComplete}
      />
    </TransformWrapper>
  );
};

interface PoiControlsProps {
  initialScale: number;
  minScale: number;
  dimControls: boolean;
}

const PoiControls: React.FC<PoiControlsProps> = ({
  initialScale,
  minScale,
  dimControls,
}) => {
  const { zoomIn, zoomOut, centerView, instance } = useControls();
  const currentScale = roundNumber(
    instance.transformState.scale,
    ROUNDING_ACCURACY
  );
  return (
    <div className={`PoiImageZoomTools ${dimControls ? "DimControls" : ""}`}>
      <Button
        colour="White"
        onClick={() => zoomIn()}
        disabled={currentScale >= MAX_SCALE}
        disabledClass="DisabledOpacity"
      >
        Zoom in
      </Button>
      <Button
        colour="White"
        onClick={() => zoomOut()}
        disabled={currentScale <= minScale}
        disabledClass="DisabledOpacity"
      >
        Zoom out
      </Button>
      {/* <Button
        colour="White"
        onClick={() => centerView()}
        disabledClass="DisabledOpacity"
      >
        Centre
      </Button> */}
      <Button
        colour="White"
        onClick={() => centerView(initialScale)}
        disabledClass="DisabledOpacity"
      >
        Reset
      </Button>
    </div>
  );
};

/**
 * `$transitionShort` out
 */
const TIMEOUTS: TransitionProps["timeout"] = {
  enter: 0,
  appear: 0,
  exit: 300,
};

interface PoiMiniMapProps {
  poiImage: React.ReactNode;
  hideMiniMap: boolean;
}

const PoiMiniMap: React.FC<PoiMiniMapProps> = ({ poiImage, hideMiniMap }) => {
  const nodeRef = useRef<HTMLDivElement>(null);
  return (
    <Transition
      nodeRef={nodeRef}
      in={!hideMiniMap}
      timeout={TIMEOUTS}
      mountOnEnter
      // Don't unmount as this is a key component of the Fullscreen POI UI
      // unmountOnExit
    >
      {(transitionState) => (
        <div ref={nodeRef} className={`PoiImageZoomMiniMap ${transitionState}`}>
          <MiniMap height={125} children={poiImage} />
        </div>
      )}
    </Transition>
  );
};

export default PoiImageZoom;
